import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRoundIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/input";
import { authClient } from "../auth";
import { useAdminFormat } from "../format";

const PASSKEYS = ["passkeys"];

/** The operator's passkeys: add one for another device, delete one that is gone. */
export function PasskeysPage() {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(null);
  const passkeys = useQuery({
    queryKey: PASSKEYS,
    queryFn: async () => (await authClient.passkey.listUserPasskeys()).data ?? [],
    refetchInterval: false,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: PASSKEYS });
  const add = useMutation({
    mutationFn: async () => {
      const { error } = await authClient.passkey.addPasskey({
        name: name.trim() || t("adminApp.passkeys.defaultName"),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setName("");
      void refresh();
      toast.success(t("adminApp.passkeys.added"));
    },
    onError: () => toast.error(t("adminApp.passkeyFailed")),
  });
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await authClient.passkey.deletePasskey({ id });
      if (error) throw error;
    },
    onSuccess: () => {
      void refresh();
      toast.success(t("adminApp.passkeys.deleted"));
    },
  });
  const list = passkeys.data ?? [];

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-sm text-muted-foreground">{t("adminApp.passkeys.body")}</p>
      <Card>
        <ul className="divide-y divide-border">
          {list.map((passkey) => (
            <li key={passkey.id} className="flex items-center gap-3 px-5 py-3">
              <KeyRoundIcon className="size-4 text-muted-foreground" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{passkey.name || passkey.id}</p>
                <p className="text-xs text-muted-foreground">
                  {t("adminApp.passkeys.created", {
                    when: format.dateTime(passkey.createdAt ? String(passkey.createdAt) : null),
                  })}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("adminApp.passkeys.delete", { name: passkey.name || passkey.id })}
                onClick={() => setDeleting({ id: passkey.id, name: passkey.name || passkey.id })}
              >
                <Trash2Icon />
              </Button>
            </li>
          ))}
          {passkeys.isSuccess && list.length === 0 ? (
            <li className="px-5 py-4 text-sm text-muted-foreground">
              {t("adminApp.passkeys.empty")}
            </li>
          ) : null}
        </ul>
      </Card>
      <form
        className="flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          add.mutate();
        }}
      >
        <div className="flex-1">
          <Label htmlFor="passkey-name">{t("adminApp.passkeys.name")}</Label>
          <Input
            id="passkey-name"
            className="mt-1.5"
            value={name}
            maxLength={60}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("adminApp.passkeys.nameHint")}
          />
        </div>
        <Button type="submit" variant="outline" disabled={add.isPending}>
          <PlusIcon />
          {t("adminApp.passkeys.add")}
        </Button>
      </form>
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t("adminApp.passkeys.deleteTitle", { name: deleting?.name ?? "" })}
        description={
          list.length <= 1 ? t("adminApp.passkeys.deleteLast") : t("adminApp.passkeys.deleteBody")
        }
        confirmLabel={t("common.delete")}
        destructive
        pending={remove.isPending}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
}
